import { DownloadOutlined } from "@ant-design/icons"
import { List, useModalForm, useTable } from '@refinedev/antd'
import {
  BaseRecord,
  getDefaultFilter,
  IResourceComponentsProps,
} from '@refinedev/core'
import { Badge, Button, Menu, MenuProps, Space, Table } from 'antd'
import { ColumnType } from 'antd/es/table'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from "react-router-dom"
import { useModalForm as useModalFormAntd } from 'sunflower-antd'

import { BalanceSelect, CurrencyBalance } from '../../components/balanceSelect/BalanceSelect'
import { NewCustomerModalCreate } from '../../components/modal/admin/addCustomer'
import { EditCustomer } from '../../components/modal/admin/editCustomer'
import { getStorageUrl } from "../../utils/hooks/useCustomer.hook"
import { useDeleteUser } from '../../utils/hooks/useDeleteUser.hook'
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook'
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook'
import { VerificationStatus } from '../../utils/hooks/useWorldCheckVerification.hook'
import { useExportExcel } from '../../utils/hooks/useExportExcel.hook'
import { onSearch } from '../../utils/list.utils'
import { confirmModalDeleteUserProps } from '../components/constants'
import { CopyButton } from '../components/copyButton'
import { Row } from '../components/interface'
import { TableForm } from '../crypto-withdrawal/tableForm'
import '../styles/index.css'
import { DeleteCustomerButton } from './deleteCustomerButton'
import { StatementReportModal } from './StatementReportModal'
import {
  verificationStatus,
  verificationStatusColors,
} from './worldCheckStatus.utils'

export const CustomerList: FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<number>()
  const [deletingId, setDeletingId] = useState<number>()
  const isAdminRole = useIsAdminRole()
  const {
    tableProps,
    searchFormProps,
    filters,
    setCurrent,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    onSearch,
  })

  const { deleteUser } = useDeleteUser()
  const { exportExcel } = useExportExcel()
  const isDeletingRef = useRef(false)

  const reloadListAfterDelete = useCallback(async () => {
    const result = await refetchList()
    const pagination = tableProps.pagination
    if (!pagination || typeof pagination === 'boolean') return

    const responseData: any = result?.data
    const pageSize = Number(pagination.pageSize || 10)
    const currentPage = Number(pagination.current || 1)
    const responseTotal = Number(
      responseData?.total ??
      responseData?.totalCount ??
      responseData?.data?.total ??
      responseData?.data?.totalCount
    )

    const total = Number.isNaN(responseTotal)
      ? Number(pagination.total || 0)
      : responseTotal
    const targetPage = Math.max(1, Math.ceil(total / pageSize))

    if (currentPage > targetPage) {
      if (setCurrent) {
        setCurrent(targetPage)
      } else {
        pagination.onChange?.(targetPage, pageSize)
      }
    }
  }, [refetchList, setCurrent, tableProps.pagination])

  const onOkDelete = useCallback(async () => {
    if (isDeletingRef.current) return
    if (deletingId === undefined) return
    isDeletingRef.current = true
    const idToDelete = deletingId
    setDeletingId(undefined)
    try {
      await deleteUser(idToDelete)
      await reloadListAfterDelete()
    } finally {
      isDeletingRef.current = false
    }
  }, [deletingId, deleteUser, reloadListAfterDelete])

  const onCancelDelete = useCallback(() => {
    setDeletingId(undefined)
  }, [])

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteUserProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  })

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification()
    }
  }, [deletingId, openNotification])

  const onClickDelete = (id: number) => () => {
    setDeletingId(id)
  }

  const handClick = (record: BaseRecord) => {
    if (record.id && isAdminRole) {
      // setEditingId(Number(record.id))
      navigate(`/customers/${record.id}`)
    }
  }

  const handleClickOnCell = (record: BaseRecord) => {
    return {
      onClick: () => handClick(record),
    }
  }

  const downloadAttachedDocuments = (id: string) => {
    getStorageUrl(id).then((url) => {
      if (url) {
        const environment = process.env.REACT_APP_ENVIRONMENT;
        if (environment === "development") {
          url = url.replace(
            "http://172.188.80.191:10000/",
            "https://azurite.dev.smart.pixitech.asia/"
          );
        }
        window.open(url, "_blank");
      }
    });
  }

  const columns: ColumnType<Row>[] = [
    {
      dataIndex: 'id',
      title: 'ID',
      render: (value, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {value && <CopyButton value={value} />}
            <div
              onClick={() => {
                handClick(record)
              }}
            >
              {value}
            </div>
          </div>
        );
      },
    },
    {
      dataIndex: 'fullName',
      title: 'Name',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: 'email',
      title: 'Email',
      render: (value, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {value && <CopyButton value={value} />}
            <div
              onClick={() => {
                handClick(record)
              }}
            >
              {value}
            </div>
          </div>
        );
      },
    },
    {
      dataIndex: 'smartUID',
      title: 'SMART UID',
      render: (value, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {value && <CopyButton value={value} />}
            <div
              onClick={() => {
                handClick(record)
              }}
            >
              {value}
            </div>
          </div>
        );
      },
    },
    {
      dataIndex: 'address',
      title: 'Address',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'website',
      title: 'Website',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'assets',
      title: 'Avl Balance',
      render: (value: CurrencyBalance[] | any, record: any) => {
        // Handle both array and other formats
        const currencies: CurrencyBalance[] = Array.isArray(value)
          ? value
          : value && typeof value === 'object'
            ? [value]
            : []

        if (!currencies || currencies.length === 0) {
          return <span>-</span>
        }

        // totalAvlBalance from API is in USD
        const totalAvlBalanceUsd = record?.totalAvlBalance

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <BalanceSelect
              currencies={currencies}
              totalAvlBalanceUsd={totalAvlBalanceUsd}
              onCurrencyChange={(currency) => {
                // Handle currency change if needed
                console.log('Selected currency:', currency)
              }}
            />
          </div>
        )
      },
    },
    {
      dataIndex: ['worldCheckVerification', 'status'],
      title: 'WorldCheck Status',
      onCell: handleClickOnCell,
      render: (value: VerificationStatus) => {
        return (
          <Badge
            color={verificationStatusColors[value]}
            text={verificationStatus[value]}
          />
        )
      },
    },
    {
      render: (value, data) => {
        return data?.onboardingFormUuid && data?.onboardingFormUuid !== ''
          ? (
            <Button
              className={"list-button"}
              icon={<DownloadOutlined style={{ fontSize: "19px" }} />}
              size="small"
              onClick={() => downloadAttachedDocuments(data.onboardingFormUuid)}
            />
          )
          : <div style={{ width: 24, height: 24 }}></div>
      },
    },
    {
      dataIndex: 'connectedToAnyEntity',
      render: (value, data) => {
        return (
          <DeleteCustomerButton
            disabled={value}
            onClick={onClickDelete(data.id)}
          />
        )
      },
    },
  ]

  const {
    close: editModalClose,
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
  } = useModalFormAntd({})

  const defaultValue = 'customers'

  const [pageType, setPageType] = useState<'customers' | 'bank-accounts'>(defaultValue)
  const [statementModalOpen, setStatementModalOpen] = useState(false)
  const FILTER_ITEMS: MenuProps["items"] = [
    {
      label: "Customer Accounts",
      key: 'customers',
    },
    {
      label: "Beneficiaries & Remitters",
      key: 'beneficiaries-remitters',
    },
  ];

  const changeFilterPageType = (item: any) => {
    const key = item.key as 'customers' | 'bank-accounts'
    setPageType(key)
    navigate(`/${key}?pageSize=10&current=1`);
  }

  const {
    modalProps: createCustomerModalProps,
    formProps: createCustomerFormProps,
    show: createCustomerModalShow,
    formLoading: createCustomerFormLoading,
    submit: createCustomerFormSubmit,
    close: createCustomerModalClose,
  } = useModalForm({
    warnWhenUnsavedChanges: false,
    action: 'create',
  })

  return (
    <>
      <List
        title='Customers'
        headerButtons={null}
      >
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter('', filters),
          }}
          onSubmit={searchFormProps.form?.submit}
          searchPlaceholder={'Search customers by name or email'}
          showDateFilter={false}
          showExportButton={true}
          onExport={async () => {
            const searchValue = searchFormProps.form?.getFieldValue('searchValue') || ''
            await exportExcel('users/export-excel', {
              ...(searchValue && { _like: searchValue }),
            })
          }}
          headerButtons={
            isAdminRole ? (
              <Space>
                <Button onClick={() => setStatementModalOpen(true)}>e-Statement</Button>
                <Button type="primary" onClick={() => createCustomerModalShow()}>
                  Create new
                </Button>
              </Space>
            ) : null
          }
        />
        <Menu
          onClick={changeFilterPageType}
          defaultSelectedKeys={[pageType]}
          mode="horizontal"
          items={FILTER_ITEMS}
        />
        <Table {...tableProps} rowKey='id' columns={columns as any} />
      </List>
      {contextHolder}
      <NewCustomerModalCreate
        closeModal={createCustomerModalClose}
        formProps={createCustomerFormProps}
        modalProps={createCustomerModalProps}
        formIsLoading={createCustomerFormLoading}
        formSubmit={createCustomerFormSubmit}
        refetchList={refetchList}
      />
      <EditCustomer
        editingId={editingId}
        changeEditingId={setEditingId}
        show={editModalShow}
        formProps={editFormProps}
        modalProps={editModalProps}
        refetchList={refetchList}
        closeModal={editModalClose}
      />
      <StatementReportModal open={statementModalOpen} onClose={() => setStatementModalOpen(false)} />
    </>
  )
}
